import type { ShortVideo } from '~/composables/shorts';
import type { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import type { LocationSlugResolution } from '~~/core/ports/repositories/PropertiesRepository';

import { buildShortsLocationPath } from '~/composables/useShortsFilters/buildShortsLocationPath';
import { resolveShortsLocationStatesForLabels } from '~/composables/useShortsFilters/resolveShortsLocationStates';
import { siteName } from '~/constants';
import { PropertyTypePlurals } from '~/core/entities/CRMEnums';
import { getLocationPlaceholder } from '~~/utils';

import { applyShortsSeoMeta } from './shorts/shortsSeo/shortsSharedSeoMeta';
import { resolveShortsOgImageUrl } from './shorts/shortsSeo/useShortsFeedSeo';

const DEFAULT_LOCATION = 'São Paulo e Curitiba';
const ptBrFormatter = new Intl.NumberFormat('pt-BR');

/** Um tipo → plural em PropertyTypePlurals; nenhum ou vários tipos → "Imóveis". */
export function getShortsSeoPropertyTypeLabel(
	types: PropertyTypeEnum[]
): string {
	if (types.length === 1) {
		return PropertyTypePlurals[types[0] as PropertyTypeEnum];
	}
	return 'Imóveis';
}

export function buildShortsFilteredTitle(
	propertyTypeLabel: string,
	location: string
): string {
	return `${propertyTypeLabel} em ${location} | Shorts | ${siteName}`;
}

export function buildShortsFilteredDescription(
	totalAvailable: number,
	propertyTypeLabel: string,
	location: string
): string {
	const formattedTotal = ptBrFormatter.format(totalAvailable);
	const typeLabelLower = propertyTypeLabel.toLowerCase();
	return `Mais de ${formattedTotal} ${typeLabelLower} em ${location}. Encontre a melhor seleção de imóveis com os especialistas do PilarHomes Shorts.`;
}

export interface ShortsSeoLocationInput {
	cities: string[];
	regions: string[];
	macroregions: string[];
	states: string[];
}

export function getShortsSeoLocationString(
	params: ShortsSeoLocationInput
): string {
	const { cities, regions, macroregions, states } = params;
	const resolvedStates = resolveShortsLocationStatesForLabels(
		macroregions,
		states
	);
	const state = resolvedStates[0] ?? '';
	const city = cities[0] ?? '';

	if (regions.length > 1 && city) {
		return state ? `${city} - ${state}` : city;
	}

	const line = getLocationPlaceholder({
		cities,
		regions,
		macroregions,
		states: resolvedStates,
		includeRegion: true,
		separator: ' - '
	});

	if (line) return line;
	return state || DEFAULT_LOCATION;
}

export function useShortsListingSeo(
	firstVideo: Ref<ShortVideo | null | undefined>,
	options: {
		propertyTypes: Ref<PropertyTypeEnum[]>;
		hasActiveFilters: Ref<boolean>;
		totalAvailable: Ref<number>;
		precisionLevel?: Ref<LocationSlugResolution['precisionLevel'] | null>;
	} & {
		cities: Ref<string[]>;
		regions: Ref<string[]>;
		macroregions: Ref<string[]>;
		states: Ref<string[]>;
	}
) {
	const locationForSeo = computed(() =>
		getShortsSeoLocationString({
			cities: options.cities.value,
			regions: options.regions.value,
			macroregions: options.macroregions.value,
			states: options.states.value
		})
	);

	const propertyTypeLabel = computed(() =>
		getShortsSeoPropertyTypeLabel(options.propertyTypes.value)
	);

	const title = computed(() => {
		if (!options.hasActiveFilters.value) {
			return `Shorts | ${siteName}`;
		}
		const location = locationForSeo.value || DEFAULT_LOCATION;
		return buildShortsFilteredTitle(propertyTypeLabel.value, location);
	});

	const description = computed(() => {
		if (!options.hasActiveFilters.value) {
			return 'Descubra uma curadoria exclusiva de imóveis de alto padrão em vídeos curtos. Explore oportunidades únicas nos melhores endereços de São Paulo e Curitiba com o PilarHomes Shorts.';
		}
		const location = locationForSeo.value || DEFAULT_LOCATION;
		return buildShortsFilteredDescription(
			options.totalAvailable.value,
			propertyTypeLabel.value,
			location
		);
	});

	const keywords = computed(() => {
		if (!options.hasActiveFilters.value) {
			return 'alto padrão, São Paulo Curitiba, PilarHomes, imóveis de alto padrão';
		}
		const location = locationForSeo.value;
		return `${propertyTypeLabel.value}, ${location}, alto padrão, à venda, PilarHomes`;
	});

	/** Mesma regra que o feed de short: só thumb do vídeo (`resolveShortsOgImageUrl`). */
	const ogImage = computed(() =>
		resolveShortsOgImageUrl(firstVideo.value?.property)
	);

	const origin = useRequestURL().origin;

	const canonicalUrl = computed(() => {
		const { path, query } = buildShortsLocationPath({
			macroregions: options.macroregions.value,
			cities: options.cities.value,
			regions: options.regions.value,
			states: options.states.value,
			propertyTypes: options.propertyTypes.value
		});
		const url = new URL(path, origin);
		const isStatePrecision = options.precisionLevel?.value === 'state';
		if (query.macroregions && isStatePrecision) {
			url.searchParams.set('macroregions', query.macroregions);
		}
		return url.toString();
	});

	useHead({
		link: [{ rel: 'canonical', href: canonicalUrl }]
	});

	applyShortsSeoMeta({
		title,
		description,
		keywords,
		ogImage
	});

	return {
		title,
		description,
		canonicalUrl
	};
}
